import React from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isAudioAttachment } from '../../../../lib/voice-message';
import { docSource, docViewerKind } from '../../../../lib/doc-viewer';
import { rendersInline } from '../../../../lib/inline-attachment';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useAttachmentContent } from '../../../../lib/use-attachment-content';
import { fillsReservedBox, isPlaceholderAttachment } from '../../../../lib/attachment-placeholder';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { extensionOf, humanSize, typeLabel } from '../../../../lib/artifact-file';
import { attachmentPalette, type AttachmentPalette, type AttachmentTone } from './palette';
import { AudioAttachmentPlayer } from './AudioAttachmentPlayer';
import { AttachmentGraphic, attachmentImageUrl, attachmentAsFile } from './AttachmentGraphic';
import { AttachmentLabel } from './AttachmentLabel';
import { InlineAttachmentPreview } from './InlineAttachmentPreview';

export function SentAttachmentList({
  attachments,
  tone = 'bubble',
  agentAuthored = false,
}: {
  attachments: MessageAttachment[];
  tone?: AttachmentTone;
  /** On the agent's side of the conversation: a single picture there keeps the box its skeleton reserved. */
  agentAuthored?: boolean;
}) {
  // Single attachment: inline-large layout. Multi-attachment: a wrapping row of uniform square
  // thumbnails — the same picture-first treatment a single file gets, just smaller.
  const palette = attachmentPalette(tone);

  if (attachments.length === 1) {
    const att = attachments[0];
    if (isAudioAttachment(att.mimeType)) return <AudioAttachmentPlayer attachment={att} tone={tone} />;
    return (
      <div style={styles.single}>
        <SingleAttachment att={att} palette={palette} agentAuthored={agentAuthored} />
      </div>
    );
  }
  return (
    <div style={styles.list}>
      {attachments.map((att, i) =>
        isAudioAttachment(att.mimeType) ? (
          <AudioAttachmentPlayer key={`${att.fileName || att.vfsPath || att.url}-${i}`} attachment={att} tone={tone} />
        ) : (
          <SentAttachmentTile key={`${att.fileName || att.vfsPath || att.url}-${i}`} att={att} palette={palette} />
        ),
      )}
    </div>
  );
}

/** Open the in-app viewer for a chip, given the attachment's resolved `content` (its URL, or the workspace
 *  file body a `vfsPath` artifact resolves to). The chip stays a real `<a href target="_blank">` when the
 *  content is a URL and we only PREVENT the default on a plain primary click — the same modifier bailout
 *  `Markdown`'s in-app links use — so cmd/ctrl/shift/alt-click and middle-click still open a tab, and a
 *  no-JS or pre-hydration click degrades to opening the raw URL.
 *
 *  A `null` kind NO LONGER falls through to the browser: every chip opens the same modal; the ones we
 *  cannot render say so and offer Download / Open in new tab, which is the same hand-off made visible and
 *  undoable. The resolved kind and content travel WITH the request — classification here has the
 *  attachment's mimeType, and the viewer's `WorkspaceFile` doesn't carry one — and the viewer is presented
 *  at the ROOT (`DocViewerHost`), so it outlives this row when an optimistic just-sent message reconciles. */
function useOpenAttachment() {
  const { logPageClickBi } = useGenericBi();
  return (att: MessageAttachment, content: string): React.MouseEventHandler<HTMLElement> =>
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      logPageClickBi({ page_name: 'chat', button_name: 'open_file', answer: att.fileName });
      e.preventDefault();
      const kind = docViewerKind(att.fileName, content || undefined, att.mimeType);
      const file = { name: att.fileName ?? '', isApp: false, content };
      openDocViewer({ file, kind, pageName: 'document_viewer' });
    };
}

/** The tilted file-type page reads right at this height in a chat row — big enough that the artwork's
 *  colour registers, small enough that the chip stays one line of text tall. */
const GRAPHIC_H = 30;

/** The multi-attachment tile: a square well the thumbnail fills, with the file-type graphic sized to sit
 *  inside it with a little air. */
const TILE = 88;
const TILE_GRAPHIC_H = 52;
/** The box a single generated image is announced in and then lands in — one size for the skeleton and the
 *  asset, so the swap never moves the row. 220 is the width `singleLink` caps any single image to. */
const RESERVED_IMAGE = 220;

function SingleAttachment({ att, palette, agentAuthored }: { att: MessageAttachment; palette: AttachmentPalette; agentAuthored: boolean }) {
  const arrived = useArrivedFromPlaceholder(att);
  if (isPlaceholderAttachment(att)) {
    return <GeneratingAttachment att={att} palette={palette} shape="single" />;
  }
  return <ResolvedSingleAttachment att={att} palette={palette} arrived={arrived} agentAuthored={agentAuthored} />;
}

function ResolvedSingleAttachment({
  att,
  palette,
  arrived,
  agentAuthored,
}: {
  att: MessageAttachment;
  palette: AttachmentPalette;
  arrived: boolean;
  agentAuthored: boolean;
}) {
  const { content, pending } = useAttachmentContent(att);
  const onClick = useOpenAttachment()(att, content);
  const isImage = att.mimeType.startsWith('image/');
  // The image itself, extracted — a reference-backed file's content is a FileRecord header FOLLOWED by the
  // URL, so handing `content` straight to an <img> renders the torn-page glyph.
  const src = attachmentImageUrl(att, content);

  // A file we can render shows its CONTENT here, not a chip that opens a window over the conversation:
  // reading the file is the point, and the popup broke the thread to do it. The chip survives for exactly
  // the files we cannot render (kind `null` — a `.zip`, a workbook), where it is still the honest
  // hand-off. An image already IS its own preview and keeps its tighter single-image layout, and audio
  // never reaches here (routed to the player above).
  if (rendersInline(att, content, pending)) {
    return <InlineAttachmentPreview att={att} content={content} />;
  }

  // An agent-made picture sits in the box its skeleton reserved, on this load and every later one, so it
  // is never smaller than the placeholder it replaced. An uploaded photo had no skeleton and keeps its aspect.
  const body =
    isImage && src ? (
      arrived ? (
        <ArrivingImage src={src} alt={att.fileName} shape="single" />
      ) : (
        <img src={src} alt={att.fileName} style={fillsReservedBox(att.mimeType, agentAuthored) ? styles.reservedImage : styles.singleImage} />
      )
    ) : (
      <>
        <AttachmentGraphic att={att} content={content} height={GRAPHIC_H} />
        <AttachmentLabel att={att} content={content} />
      </>
    );
  return (
    <AttachmentChip att={att} content={content} pending={pending} onClick={onClick} style={styles.singleLink(palette, isImage)}>
      {body}
    </AttachmentChip>
  );
}

/** One file in a multi-attachment message: a square thumbnail showing the file itself — its picture when it
 *  has one, else the shared `FileThumb` graphic (a document's first lines, or its file-type page), so a tile
 *  is never an empty box. The name sits under the tile on one line; the tile is the thing you read. */
function SentAttachmentTile({ att, palette }: { att: MessageAttachment; palette: AttachmentPalette }) {
  const arrived = useArrivedFromPlaceholder(att);
  if (isPlaceholderAttachment(att)) {
    return <GeneratingAttachment att={att} palette={palette} shape="tile" />;
  }
  return <ResolvedAttachmentTile att={att} palette={palette} arrived={arrived} />;
}

function ResolvedAttachmentTile({ att, palette, arrived }: { att: MessageAttachment; palette: AttachmentPalette; arrived: boolean }) {
  const { content, pending } = useAttachmentContent(att);
  const onClick = useOpenAttachment()(att, content);
  const isImage = att.mimeType.startsWith('image/');
  const src = attachmentImageUrl(att, content);
  const body = (
    <>
      <span style={styles.tileWell(palette)}>
        {isImage && src ? (
          arrived ? (
            <ArrivingImage src={src} alt={att.fileName} shape="tile" />
          ) : (
            <img src={src} alt={att.fileName} style={styles.tileImage} />
          )
        ) : (
          <AttachmentGraphic att={att} content={content} height={TILE_GRAPHIC_H} />
        )}
      </span>
      <span style={styles.tileName}>{att.fileName}</span>
    </>
  );
  return (
    <AttachmentChip att={att} content={content} pending={pending} onClick={onClick} style={styles.tileLink(palette)}>
      {body}
    </AttachmentChip>
  );
}

/**
 * Whether this chip was showing a placeholder a moment ago, so the asset can settle in rather than
 * appear between two frames. True only for a swap the reader is watching: a chip that loads already
 * resolved (scroll-back, a fresh conversation) never fades.
 */
/**
 * The image that replaces a placeholder. The quartz keeps running UNDER it until the bytes have decoded,
 * and only then does the picture fade up over it — so the swap is one continuous motion and never a blank
 * frame, a spinner, or a pop. A plain `<img>` given a new `src` paints nothing until it has loaded, which
 * is the flicker this replaces. The box IS the placeholder's footprint, before and after the load: the
 * picture fills it (cover), so the asset is exactly the size of the skeleton and nothing around it moves.
 */
function ArrivingImage({ src, alt, shape }: { src: string; alt: string; shape: 'single' | 'tile' }) {
  const [loaded, setLoaded] = React.useState(false);
  return (
    <span style={shape === 'tile' ? styles.arrivingTile : styles.arrivingSingle}>
      {!loaded && (
        <span className="orion-attachment-generating" style={{ ...styles.arrivingUnderlay, ['--gen-blur' as string]: shape === 'tile' ? '4px' : '9px' }}>
          <i />
          <i />
          <i />
        </span>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        style={{ ...styles.arrivingImage, opacity: loaded ? 1 : 0 }}
        className={loaded ? 'orion-attachment-arrive' : undefined}
      />
    </span>
  );
}

function useArrivedFromPlaceholder(att: MessageAttachment): boolean {
  const wasPlaceholder = React.useRef(isPlaceholderAttachment(att));
  if (isPlaceholderAttachment(att)) {
    wasPlaceholder.current = true;
  }
  return wasPlaceholder.current && !isPlaceholderAttachment(att);
}

/**
 * A file the agent has announced and something is still generating: the file's own shape, shimmering,
 * with its name.
 *
 * Not interactive and not the file-type graphic — there is nothing to open, and the graphic would claim
 * a file that does not exist yet, which is the inert-looking-finished chip the rules below exist for.
 */
function GeneratingAttachment({ att, palette, shape }: { att: MessageAttachment; palette: AttachmentPalette; shape: 'single' | 'tile' }) {
  // A placeholder takes the shape the FINISHED file will take, and for a single file that depends on its
  // type: an image resolves to a picture, so it reserves one; a deck or a document resolves to a named
  // chip, so a big image-shaped box would be the wrong promise and would collapse when it filled.
  const isImage = att.mimeType.startsWith('image/');
  const blur = shape === 'tile' || !isImage ? '4px' : '9px';
  const light = (
    <span className="orion-attachment-generating" style={{ ...styles.generatingFill, ['--gen-blur' as string]: blur }}>
      <i />
      <i />
      <i />
    </span>
  );
  if (shape === 'tile') {
    return (
      <span title={att.fileName} style={styles.tileLink(palette)} aria-busy="true">
        <span style={styles.tileWell(palette)}>{light}</span>
        <span style={styles.tileName}>{att.fileName}</span>
      </span>
    );
  }
  if (!isImage) {
    return (
      <span title={att.fileName} style={styles.singleLink(palette, false)} aria-busy="true">
        <span style={styles.generatingGlyph(palette)}>{light}</span>
        <span style={styles.generatingName}>{att.fileName}</span>
      </span>
    );
  }
  return (
    <span title={att.fileName} style={styles.generatingSingle(palette)} aria-busy="true">
      {light}
    </span>
  );
}

/** The clickable chip, in three shapes by what we can do with the attachment:
 *   - the content resolves to a URL → a real `<a href>` so modified/middle clicks still open a tab;
 *   - anything else already resolved → a non-anchor button (an `<a href="">` would navigate to the current
 *     page and reload the whole app);
 *   - still resolving → a plain, non-interactive chip for the moment it takes.
 *
 *  A `vfsPath` artifact — a file the agent authored — carries `url: ''` and its location in `vfsPath`;
 *  `useAttachmentContent` resolves it to the workspace file the gallery shows (see `vfs-attachment.ts`).
 *
 *  ⚠️ The URL test is `docSource`, NOT `isBareHttpsUrl` on the raw content, and the button is NOT gated on
 *  `docViewerKind`. Both were bugs, and they stacked into an inert chip for every agent-authored file:
 *   - A promoted artifact's stored content is the `<!--sym-file:…-->` FileRecord header followed by the
 *     URL (harness `manage-artifacts-tool.ts` `encodeFileRecordContent`), so it is never a BARE https URL
 *     and never became an anchor — even though the header's own `rawUrl` says exactly where the file is.
 *     `docSource` is the load plan the viewer itself runs, so the chip and the viewer now agree.
 *   - A kind the viewer deliberately does not render (a spreadsheet — see `doc-viewer.ts` on the SheetJS
 *     CVEs — a `.zip`, anything unknown) then fell through to a non-interactive `<span>`. Those files are
 *     supposed to KEEP the download hand-off, and the viewer already has an honest "no preview" state with
 *     Download / Open in new tab. Opening that beats a chip that looks like a file and does nothing —
 *     the same rule `use-task-detail-modal` states for a failed resolve. */
function AttachmentChip({
  att,
  content,
  pending,
  onClick,
  style,
  children,
}: {
  att: MessageAttachment;
  content: string;
  pending: boolean;
  onClick: React.MouseEventHandler<HTMLElement>;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  const source = docSource(content);
  if (source?.kind === 'remote') {
    return (
      <a href={source.url} target="_blank" rel="noreferrer" title={att.fileName} style={style} onClick={onClick}>
        {children}
      </a>
    );
  }
  if (pending) {
    return (
      <span title={att.fileName} style={style}>
        {children}
      </span>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      title={att.fileName}
      style={{ ...style, cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent<HTMLElement>);
        }
      }}
    >
      {children}
    </span>
  );
}

const styles = {
  single: { marginTop: 8 } satisfies React.CSSProperties,
  singleLink: (palette: AttachmentPalette, isImage: boolean): React.CSSProperties => ({
    // An image wrapper shrink-wraps its thumbnail (inline-block) — NOT a flex row: as a flex
    // item the <img> takes its main-axis width from the intrinsic width while `max-height` clamps
    // the cross axis independently, which breaks the aspect ratio and squishes the photo.
    display: isImage ? 'inline-block' : 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: isImage ? 0 : '6px 10px',
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    color: palette.fg,
    fontSize: 12,
    textDecoration: 'none',
    maxWidth: 220,
    overflow: 'hidden',
  }),
  // width/height auto + both max caps → the browser scales the image to fit the box while
  // preserving its natural aspect ratio (portrait renders tall, landscape wide — never cropped).
  //
  // The WIDTH rule is the document card's (`InlineAttachmentPreview` — the bubble's width), because a photo
  // and a document card are the same object to a reader: an image rendered narrower than the card above it
  // reads as a different kind of thing. Reported on the phones, and true here for the same reason. The
  // height cap is the document preview's, so neither can outgrow the other.
  singleImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 320,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: 8,
  } satisfies React.CSSProperties,
  label: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' } satisfies React.CSSProperties,
  labelName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 'var(--sym-weight-medium)',
  } satisfies React.CSSProperties,
  labelMeta: {
    fontSize: 11,
    lineHeight: '14px',
    color: 'rgba(var(--ink-rgb), 0.55)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies React.CSSProperties,
  list: { marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 } satisfies React.CSSProperties,
  tileLink: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: TILE,
    color: palette.fg,
    fontSize: 11,
    textDecoration: 'none',
  }),
  tileWell: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: TILE,
    height: TILE,
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    overflow: 'hidden',
  }),
  // Cover, not contain: at this size a letterboxed photo reads as a smaller box inside a box.
  tileImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } satisfies React.CSSProperties,
  generatingFill: { display: 'block', width: '100%', height: '100%' } satisfies React.CSSProperties,
  // The arriving image's box: the placeholder's own footprint, held before AND after the bytes decode, with
  // the quartz running inside until the picture fades up to fill it.
  arrivingSingle: {
    position: 'relative',
    display: 'block',
    width: RESERVED_IMAGE,
    height: RESERVED_IMAGE,
    overflow: 'hidden',
    borderRadius: 8,
  } satisfies React.CSSProperties,
  arrivingTile: { position: 'relative', display: 'block', width: '100%', height: '100%', overflow: 'hidden' } satisfies React.CSSProperties,
  arrivingUnderlay: { position: 'absolute', inset: 0, display: 'block' } satisfies React.CSSProperties,
  arrivingImage: { position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', objectFit: 'cover' } satisfies React.CSSProperties,
  // A generated image on a later load: the same box it arrived in, so a reload never resizes it.
  reservedImage: { display: 'block', width: RESERVED_IMAGE, height: RESERVED_IMAGE, objectFit: 'cover', borderRadius: 8 } satisfies React.CSSProperties,
  // The chip-shaped placeholder: the graphic's own footprint, so the row is the height it will be once
  // the file arrives and the name is readable the whole time it is waiting.
  generatingGlyph: (palette: AttachmentPalette): React.CSSProperties => ({
    position: 'relative',
    display: 'block',
    width: GRAPHIC_H,
    height: GRAPHIC_H,
    flexShrink: 0,
    borderRadius: 6,
    overflow: 'hidden',
    background: palette.tileBg,
  }),
  generatingName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } satisfies React.CSSProperties,
  // The space the file will take, and then keeps: the asset lands in this same square (`arrivingSingle`,
  // `reservedImage`), so skeleton and picture are always one size.
  generatingSingle: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'block',
    width: RESERVED_IMAGE,
    height: RESERVED_IMAGE,
    overflow: 'hidden',
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 8,
  }),
  tileName: {
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '14px',
  } satisfies React.CSSProperties,
};
